<script lang="ts">
  import NavigationLoading from './NavigationLoading.svelte'
  import { page, Link } from '@inertiajs/svelte'
  import ChevronDown from '@lucide/svelte/icons/chevron-down'
  import Shield from '@lucide/svelte/icons/shield'
  import Compass from '@lucide/svelte/icons/compass'
  import Settings from '@lucide/svelte/icons/settings'
  import Users from '@lucide/svelte/icons/users'
  import Award from '@lucide/svelte/icons/award'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import TrendingUp from '@lucide/svelte/icons/trending-up'
  import Star from '@lucide/svelte/icons/star'
  import Shuffle from '@lucide/svelte/icons/shuffle'
  import Share from '@lucide/svelte/icons/share'
  import UserPlus from '@lucide/svelte/icons/user-plus'
  import Check from '@lucide/svelte/icons/check'
  import Camera from '@lucide/svelte/icons/camera'
  import Button from './ui/Button.svelte'
  import Dropdown from './Dropdown.svelte'
  import MobileAlgoliaSearch from './MobileAlgoliaSearch.svelte'
  import {
    root_path,
    discover_path,
    settings_path,
    sign_in_path,
    sign_up_path,
    clubs_path,
    challenges_path,
    featured_path,
    bestsellers_path,
    random_path,
    catalog_books_path,
    admin_root_path,
    recommendations_path,
    new_ocr_scan_path,
  } from '@/routes'

  let currentUser = $derived(page.props.current_user)
  let catalogPath = $derived(
    currentUser?.is_superuser ? admin_root_path() : catalog_books_path()
  )
  let algolia = $derived(
    page.props.algolia as
      { application_id: string; index_name: string } | undefined
  )
  let inviteCopied = $state(false)

  type MenuItem = {
    href: string
    label: string
    icon: typeof BookOpen
  }

  const menuItems: MenuItem[] = [
    { href: discover_path(), label: 'Discover', icon: Compass },
    { href: recommendations_path(), label: 'Recommendations', icon: BookOpen },
    { href: clubs_path(), label: 'Clubs', icon: Users },
    { href: challenges_path(), label: 'Challenges', icon: Award },
    { href: featured_path(), label: 'Featured', icon: Star },
    { href: bestsellers_path(), label: 'Bestsellers', icon: TrendingUp },
    { href: random_path(), label: 'Find Next Read', icon: Shuffle },
    { href: new_ocr_scan_path(), label: 'Scan Book', icon: Camera },
    { href: settings_path(), label: 'Settings', icon: Settings },
  ]

  function shareCurrentPage() {
    if (typeof window === 'undefined') return

    const shareUrl = new window.URL(window.location.href)
    // Superusers get their ref link added to shares
    if (currentUser?.is_superuser && currentUser?.username) {
      shareUrl.searchParams.set('ref', currentUser.username)
    }
    const url = shareUrl.toString()

    if (window.navigator.share) {
      window.navigator
        .share({
          title: document.title,
          url: url,
        })
        .catch((err) => {
          console.log('Share failed:', err)
        })
    } else {
      window.navigator.clipboard.writeText(url)
    }
  }

  function copyInviteLink() {
    if (typeof window === 'undefined') return
    if (!currentUser?.username) return

    const inviteUrl = new window.URL(window.location.origin + '/sign_up')
    inviteUrl.searchParams.set('ref', currentUser.username)

    window.navigator.clipboard.writeText(inviteUrl.toString())
    inviteCopied = true
    setTimeout(() => {
      inviteCopied = false
    }, 1500)
  }
</script>

<header
  class="md:hidden bg-white dark:bg-gray-800 border-b border-neutral-200 dark:border-gray-700 z-40 {currentUser
    ? 'shrink-0'
    : 'sticky top-0'}"
>
  <div
    class="flex justify-between items-center gap-1 h-14 px-2 min-[375px]:px-4"
  >
    <div class="flex shrink-0 items-center gap-0 min-[375px]:gap-1">
      <Link
        href={root_path()}
        class="whitespace-nowrap text-lg min-[375px]:text-xl min-[390px]:text-2xl font-bookshop tracking-tight text-neutral-800 dark:text-gray-100"
      >
        Reading Now
      </Link>
      <NavigationLoading />
      {#if currentUser?.can_manage_catalog}
        <Link
          href={catalogPath}
          aria-label={currentUser.is_superuser ? 'Admin' : 'Catalog'}
          title={currentUser.is_superuser ? 'Admin' : 'Catalog'}
          class="flex size-8 shrink-0 items-center justify-center rounded-md text-neutral-500 dark:text-gray-400 hover:text-neutral-700 dark:hover:text-gray-300"
        >
          <Shield size={16} aria-hidden="true" />
        </Link>
      {/if}
    </div>

    {#if currentUser}
      <div class="flex shrink-0 items-center gap-0 min-[375px]:gap-1">
        <!-- Algolia Search -->
        {#if algolia}
          <MobileAlgoliaSearch
            applicationId={algolia.application_id}
            indexName={algolia.index_name}
          />
        {/if}

        <!-- Share Button -->
        <button
          type="button"
          onclick={shareCurrentPage}
          class="flex size-9 items-center justify-center text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 rounded-md transition"
          aria-label="Share this page"
        >
          <Share size={20} />
        </button>

        <!-- Invite Button (Superusers only) -->
        {#if currentUser.is_superuser}
          <button
            type="button"
            onclick={copyInviteLink}
            class="flex size-9 items-center justify-center text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 rounded-md transition"
            aria-label="Copy invite link"
          >
            {#if inviteCopied}
              <Check size={20} />
            {:else}
              <UserPlus size={20} />
            {/if}
          </button>
        {/if}

        <Dropdown width="w-48" position="right" ariaLabel="Open menu">
          {#snippet trigger()}
            <div
              class="flex size-9 items-center justify-center text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 rounded-md transition"
            >
              <ChevronDown size={20} />
            </div>
          {/snippet}
          {#snippet content()}
            <div class="py-1">
              {#each menuItems as item (item.href)}
                <Link
                  href={item.href}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <item.icon size={16} />
                  {item.label}
                </Link>
              {/each}
            </div>
          {/snippet}
        </Dropdown>
      </div>
    {:else}
      <div class="flex items-center space-x-2">
        <Link
          href={sign_in_path()}
          class="px-3 py-2 text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white"
        >
          Sign in
        </Link>
        <Link href={sign_up_path()}>
          <Button>Sign up</Button>
        </Link>
      </div>
    {/if}
  </div>
</header>
